import { useRef, FormEvent, KeyboardEvent, ChangeEvent } from 'react'
import classNames from 'classnames'

interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onPaste: (e: React.ClipboardEvent) => void
  isLoading: boolean
}

/**
 * Chat input component with auto-resize textarea and send button.
 * Handles Enter key submission and Shift+Enter for new lines.
 */
export function ChatInput({
  value,
  onChange,
  onSubmit,
  onPaste,
  isLoading
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit()
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit()
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
    // Auto-resize textarea
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200).toString()}px`
  }

  const handlePasteWrapper = (e: React.ClipboardEvent) => {
    onPaste(e)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className='relative'>
        <div className='flex items-center rounded-lg border border-stone-300/70 bg-white/50 px-4 py-3 dark:border-stone-600 dark:bg-white/5'>
          {/* Input field */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePasteWrapper}
            placeholder='Ask anything'
            className={classNames(
              'flex-1 resize-none bg-transparent px-1 py-1',
              'text-sm placeholder-stone-500',
              'text-stone-900 dark:text-stone-100',
              'focus:outline-none',
              'dark:placeholder-stone-400'
            )}
            rows={1}
            disabled={isLoading}
          />

          {/* Send button - always visible but disabled when no text */}
          <button
            type='submit'
            disabled={!value.trim() || isLoading}
            className={classNames(
              'ml-3 flex h-8 w-8 items-center justify-center rounded-full transition-colors',
              value.trim() && !isLoading
                ? 'bg-amber-600 text-white dark:text-amber-950'
                : 'bg-stone-500/20 text-stone-500 dark:bg-stone-600 dark:text-stone-400'
            )}
          >
            <i className='fa-solid fa-arrow-up text-sm' />
          </button>
        </div>
      </div>
    </form>
  )
}